import { useState, useEffect } from "react";
import { Trophy, FileText, Calendar, User, ArrowLeft, ChevronRight } from "lucide-react";
import { getBlogPosts, getSettings } from "../lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, settingsRes] = await Promise.all([
        getBlogPosts(),
        getSettings(),
      ]);
      setPosts(postsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const tournamentLogo = settings?.logo;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Single post view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedPost(null)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                {tournamentLogo ? (
                  <img
                    src={tournamentLogo}
                    alt="Logo"
                    className="w-10 h-10 rounded-xl object-cover border border-primary/30"
                  />
                ) : (
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/30">
                    <Trophy className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div>
                  <h1 className="font-heading text-sm font-bold uppercase tracking-wider">
                    GOODWILLERS
                  </h1>
                  <p className="text-xs text-muted-foreground">Tournament Blog</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="max-w-4xl mx-auto p-4 md:p-8">
          {selectedPost.image && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8">
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            {selectedPost.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {selectedPost.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(selectedPost.created_at)}
            </span>
          </div>

          <div className="prose prose-invert max-w-none">
            {selectedPost.content.split("\n").map((paragraph, i) => (
              <p key={i} className="mb-4 text-foreground/90 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </div>
    );
  }

  // Blog list view
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-4">
            {tournamentLogo ? (
              <img
                src={tournamentLogo}
                alt="Logo"
                className="w-12 h-12 rounded-xl object-cover border border-primary/30"
              />
            ) : (
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/30">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
            )}
            <div className="text-center">
              <h1 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-wider">
                GOODWILLERS
              </h1>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                Tournament Blog
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-6 h-6 text-primary" />
          <h2 className="font-heading text-2xl font-bold uppercase tracking-wider">
            Latest Articles
          </h2>
        </div>

        {posts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, index) => (
              <article
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="bg-card border border-border rounded-xl overflow-hidden cursor-pointer card-hover animate-fade-in opacity-0"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`blog-post-${post.id}`}
              >
                {post.image ? (
                  <div className="aspect-video">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-secondary flex items-center justify-center">
                    <FileText className="w-12 h-12 text-muted-foreground/30" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {post.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </span>
                    <span className="flex items-center gap-1 text-primary">
                      Read more <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold uppercase mb-2">
              No Articles Yet
            </h3>
            <p className="text-muted-foreground">
              Check back soon for tournament updates and news!
            </p>
          </div>
        )}

        {/* Back Link */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back to Tournament
          </a>
        </div>
      </main>
    </div>
  );
}
